/* eslint-disable import/no-cycle */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import update from 'immutability-helper';
import { debounce, isEmpty, isEqual } from 'lodash';
import { Spinner } from 'react-bootstrap';

import { useStore } from '../../../../core/context/store';
import { useInterval } from '../../../../core/hooks';
import { DropdownNavItem } from './dropdown-nav-item';
import { MenuItem, SessionProps, IScoutImage, ParamArrProps, SimulatorStatus, ScanResultItem } from './models';
import { ISequenceItem } from '../../models';
import { NavItem } from './nav-item';
import { IsocenterModal } from '../isocenter/isocenter-modal';
import { MotionModal } from './motion-modal';
import { DefaultCardiacPhaseModal } from './default-cardiac-phase-modal';
import { collectionActions, sessionActions, simulationActions, standardSequencesActions } from '../../actions';
import { simulatorNavDropdownActions } from '../../constants/simulator-nav-constants';
import { REAL_MRI_ANATOMICAL_MODEL, isocenterTypeConstants, renderAreas } from '../../constants/simulator-constants';

import Logo from '../../../../assets/images/corsmed-logo-svg.svg';
import { ApiService } from '../../../../core/services';
import simulatorConfig from '../../constants/simulator-config-constants';
import { ScannerSettingsModal } from './scanner-settings-modal';
import { PatientSettingsModal } from './patient-settings-modal';
import { SimulationSettingsModal } from './simulation-settings-modal';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { meActions } from '../../../courses/actions';
import { anatomicalModelsActions } from '../../../lab/actions';
import {
  CoilsProps,
  ExperimentConfiguration,
  IAnatomicalModel,
  SessionConfigItem,
  UniversalParameterProps,
} from '../../models';
import { useAuthState } from '../../../../core/context/auth';
import { decodeToken } from '../../../../core/lib/jwt';
import anatomicalModelFeaturesConstants from '../../constants/anatomical-model-constants';
import { ChatButton } from './chat-button';
import { CollectionItem, SimulatorStatusV2 } from '../simulator/models';

export interface MenuProps {
  items: MenuItem[];
}

export interface TissueCharacteristicsProps {
  tissue_characteristic_id: number;
  lab_id: number;
  t1: number;
  t2: number;
}

interface RouteParams {
  sessionId?: string;
}

const MOTION_NAMES: string[] = [
  simulatorConfig.MOTION_ROT_ANGLE,
  simulatorConfig.MOTION_ROT_FREQ,
  simulatorConfig.MOTION_TRANS_MAGN,
  simulatorConfig.MOTION_TRANS_FREQ,
  simulatorConfig.MOTION_TRANS_AXIS,
];

const CONTRAST_NAMES: string[] = [
  simulatorConfig.ACTIVATE_CONTRAST,
  simulatorConfig.CONTRAST_CONCENTRATION,
  simulatorConfig.CONTRAST_VOLUME,
  simulatorConfig.TIME_AFTER_CONTRAST,
];
const DEFAULT_CARDIAC_CONFIG_NAMES: string[] = [simulatorConfig.DEFAULT_CARDIAC_PHASE];

export const Menu: React.FC<MenuProps> = ({ items }: MenuProps): React.ReactElement => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [activeSessionStore, activeSessionStoreActions] = useStore<SessionProps | SessionConfigItem | any>(
    'active-session'
  );
  const [anatomicalModelStore, anatomicalModelStoreActions] = useStore<IAnatomicalModel>('active-anatomical-model');
  const [simulatorStatusStore] = useStore<SimulatorStatus>('simulator-status');

  const [, pulseSeqCollectionActiveStoreActions] = useStore<CollectionItem | null>('pulse-sequences-collection-active');
  const [pulseSeqCollectionsStore, pulseSeqCollectionsStoreActions] = useStore<CollectionItem[] | null>(
    'pulse-sequences-collections'
  );
  const [, pulseSeqCurrentGroupStoreActions] = useStore<CollectionItem[] | null>(
    'pulse-sequences-collections-current-group'
  );

  const { data: simStatus } = simulatorStatusStore;
  const [, leftReconImagesStoreActions] = useStore<IScoutImage[]>(`images-${renderAreas.leftViewer}`);
  const [, centerReconImagesStoreActions] = useStore<IScoutImage[]>(`images-${renderAreas.centerViewer}`);
  const [, rightReconImagesStoreActions] = useStore<IScoutImage[]>(`images-${renderAreas.rightViewer}`);
  const [closing, setClosing] = useState<boolean>(false);
  const [, paramsListStoreActions] = useStore<ParamArrProps | null>('params-list');
  const [, droppedSequenceStoreActions] = useStore<ISequenceItem | null>('drop-sequence-viewer-1', null);
  const [, modalPWStoreActions] = useStore<boolean>('modal-perspective-view');
  const [, scanResultsStoreActions] = useStore<Array<ScanResultItem>>('scan-results');
  const [, simulatorStatusStoreActions] = useStore<SimulatorStatusV2>('simulator-status');
  const { data: sessionData } = activeSessionStore;
  const { data: anatomicalModel, loading: anatomicalModelLoading } = anatomicalModelStore;
  const { data: collectionsData } = pulseSeqCollectionsStore;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [additionalCoils, setAdditionalCoils] = useState<any>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [tissueCharacteristics, setTissueCharacteristics] = useState<any>({
    chars: [],
    fetched: false,
  });
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const history = useHistory();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [resetDisabled, setResetDisabled] = useState(false);
  const [motionModalVisible, setMotionModalVisible] = useState(false);
  const [defaultCardiacPhaseModalVisible, setDefaultCardiacPhaseModalVisible] = useState(false);
  const [scannerSettingsModalVisible, setScannerSettingsModalVisible] = useState(false);
  const [patientSettingsModalVisible, setPatientSettingsModalVisible] = useState(false);
  const [simulationSettingsModalVisible, setSimulationSettingsModalVisible] = useState(false);
  const [labAssignmentsModalVisible, setLabAssignmentsModalVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState({
    isVisible: false,
    type: '',
  });
  const navRef = useRef<HTMLElement>(null);

  const { accessToken } = useAuthState();
  const loggedUserId = accessToken && decodeToken(accessToken).uid;

  const [isocenterInitialValue, setIsocenterInitialValue] = useState(0);
  const [initialType, setInitialType] = useState('');
  const [motionParameters, setMotionParameters] = useState<ExperimentConfiguration[]>([]);
  const [contrastParameters, setContrastParameters] = useState<ExperimentConfiguration[]>([]);
  const [defaultCardiacPhaseParameters, setDefaultCardiacPhaseParameters] = useState<ExperimentConfiguration[]>([]);

  const params = useParams<RouteParams>();
  const sessionId = params.sessionId ? parseInt(params.sessionId, 10) : -1;

  const [visibleMenu, setVisibleMenu] = useState('');

  const onCloseSessionHandler = () => {
    // eslint-disable-next-line no-alert
    const r = window.confirm('Active session will be closed, are you sure you want to leave?');
    if (r === true) {
      setClosing(true);
      activeSessionStoreActions.dispatch(
        sessionActions.closeSession(sessionId, () => {
          setClosing(false);
          history.push('/courses');
          activeSessionStoreActions.dispatch(standardSequencesActions.clearSessionData());
        })
      );
    } else {
      // Return user to simulator page
      history.push(`/session/${sessionId}/simulator`);
    }
  };

  const [runningItemStore, runningItemStoreActions] = useStore<ISequenceItem>(
    'running-experiment',
    {} as ISequenceItem
  );
  const { data: runningItem } = runningItemStore;

  // disable reset button if experiment is running
  useInterval(() => {
    if (simStatus && simStatus.message === 'Running experiment...') setResetDisabled(true);
    else setResetDisabled(false);
  }, 1000);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onViewAssignmentsHandler = () => {
    scanResultsStoreActions.dispatch(meActions.getMyScanResults());
    setLabAssignmentsModalVisible(!labAssignmentsModalVisible);
  };

  const onResetHandler = () => {
    // eslint-disable-next-line no-alert
    const r = window.confirm(
      'Active session will be reset, and all protocols are going to be removed. Are you sure you want to continue?'
    );
    if (r === true) {
      droppedSequenceStoreActions.setValue(null);
      paramsListStoreActions.setValue(null);
      leftReconImagesStoreActions.setValue([]);
      centerReconImagesStoreActions.setValue([]);
      rightReconImagesStoreActions.setValue([]);
      if (runningItem && runningItem?.experimentId) {
        simulatorStatusStoreActions.dispatch(simulationActions.cancelExperiment(sessionId, runningItem.experimentId));
        runningItemStoreActions.reset();
      }
      activeSessionStoreActions.dispatch(sessionActions.resetSession(sessionId));
      activeSessionStoreActions.dispatch(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        standardSequencesActions.getActiveSession(sessionId, history, sessionData as any, true)
      );
      pulseSeqCollectionActiveStoreActions.setValue(null);
      pulseSeqCollectionsStoreActions.setValue(null);
      pulseSeqCurrentGroupStoreActions.setValue(null);
    }
  };

  const newScanDisabled = !!runningItem && !!runningItem?.experimentId;

  const onNewScanHandler = () => {
    droppedSequenceStoreActions.setValue(null);
    paramsListStoreActions.setValue(null);
    leftReconImagesStoreActions.setValue([]);
    centerReconImagesStoreActions.setValue([]);
    rightReconImagesStoreActions.setValue([]);

    const numberOfCollections = collectionsData?.length || 0;

    pulseSeqCollectionActiveStoreActions.dispatch(
      collectionActions.createNewCollection(sessionId, `Scan ${numberOfCollections + 1}`, () =>
        pulseSeqCollectionsStoreActions.dispatch(collectionActions.getCollections(sessionId))
      )
    );
  };

  const getBrainModelCoils = useCallback(async () => {
    const result = await ApiService.get(`/v1/sessions/${sessionId}/coils`);
    setAdditionalCoils(result.data as CoilsProps[]);
  }, [sessionId]);

  const getTissueCharacteristics = useCallback(async () => {
    if (sessionData) {
      const result = await ApiService.get<string>(`/v1/tissue-characteristics/${sessionData?.lab?.id}/configuration`);
      setTissueCharacteristics({ chars: result.data, fetched: true });
    }
  }, [sessionData]);

  const resetTissueCharacteristicsHandler = useCallback(async () => {
    await ApiService.delete<string>(`/v1/tissue-characteristics/${sessionData?.lab?.id}/reset-configuration`);

    // update latest data
    getTissueCharacteristics();
  }, [sessionData, getTissueCharacteristics]);

  const submitCellHandler = async (type: string, t1: number, t2: number) => {
    if (type === '') return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const foundTissue = tissueCharacteristics?.chars.find((tissue: any) => tissue.type === type);
    const formattedPayload = {
      tissue_characteristic_id: foundTissue.id,
      lab_id: sessionData?.lab?.id,
      t1,
      t2,
    };

    await ApiService.post(`/v1/tissue-characteristics/configuration`, formattedPayload);

    // update latest data
    getTissueCharacteristics();
  };

  useEffect(() => {
    if (sessionData?.lab?.anatomicalModelId && !anatomicalModelLoading && !anatomicalModel) {
      anatomicalModelStoreActions.dispatch(
        anatomicalModelsActions.getById(Number(loggedUserId), sessionData.lab.anatomicalModelId)
      );
    }
    // eslint-disable-next-line
  }, [sessionData?.lab?.anatomicalModelId]);

  useEffect(() => {
    if (isEmpty(additionalCoils)) {
      getBrainModelCoils();
    }
  }, [getBrainModelCoils, additionalCoils]);

  useEffect(() => {
    if (isEmpty(tissueCharacteristics.chars) && !tissueCharacteristics.fetched) {
      getTissueCharacteristics();
    }
  }, [getTissueCharacteristics, tissueCharacteristics]);

  const isLiteSim =
    sessionData?.lab?.anatomicalModelId && sessionData?.lab?.anatomicalModelId === REAL_MRI_ANATOMICAL_MODEL;

  useEffect(() => {
    if (!isEmpty(items)) {
      if (isLiteSim) {
        const liteSimeMenuitems = items
          .map((item) => {
            if (item.title === 'Tools') {
              return {
                ...item,
                subMenu: item.subMenu?.filter((subItem) => subItem.title === 'Image viewer (Ctrl+Shift+2)'),
              };
            }
            return item;
          })
          .filter((menuItem) => menuItem.title === 'Tools');
        setMenuItems(liteSimeMenuitems);
      } else {
        setMenuItems(items);
      }
    }
  }, [items, isLiteSim]);

  const updateMenuItems = useCallback(() => {
    if (!isEmpty(additionalCoils)) {
      // not the most elegant solution, but it works :/
      const newMenuItems = update(menuItems, {
        [menuItems.findIndex((f) => f.title === 'Scanner settings')]: {
          $apply: (menuItem: MenuItem) => {
            const newSubMenues = menuItem?.subMenu?.map((subMenuItem: MenuItem) => {
              if (subMenuItem.title === 'Coil') {
                const subItems = subMenuItem?.subMenu?.concat(
                  additionalCoils
                    .map((m: CoilsProps) => {
                      if (!subMenuItem.subMenu?.some((s) => s.name === `coil__${m.unique_name}`)) {
                        return {
                          title: m.shown_name,
                          action: 'true',
                          name: `coil__${m.unique_name}`,
                          value: m.unique_name,
                          configurationName: simulatorConfig.COIL,
                        };
                      }
                      return null;
                    })
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    .filter((f: any) => !!f)
                );
                return { ...subMenuItem, subMenu: subItems };
              }
              return subMenuItem;
            });

            return { ...menuItem, subMenu: newSubMenues } as MenuItem;
          },
        },
      });

      if (!isEqual(newMenuItems, menuItems)) {
        setMenuItems(newMenuItems);
      }
    }
  }, [additionalCoils, menuItems]);

  useEffect(() => {
    updateMenuItems();
  }, [updateMenuItems]);

  useEffect(() => {
    const updatedMotionParameters: ExperimentConfiguration[] = [];
    const updatedDefaultCardiacPhaseParameters: ExperimentConfiguration[] = [];
    const updatedContrastParameters: ExperimentConfiguration[] = [];

    sessionData?.configuration?.map((item: ExperimentConfiguration) => {
      // check if selected region is set and has value
      if (item.name === simulatorConfig.SELECTED_REGION && parseInt(item.selectedValue, 10) > 0) {
        setInitialType(isocenterTypeConstants.regional);
        if (modalVisible?.type === isocenterTypeConstants.regional) {
          setIsocenterInitialValue(parseInt(item.selectedValue, 10));
        } else setIsocenterInitialValue(0);
        return item;
      }
      // else return whole body value
      if (item.name === simulatorConfig.ISOCENTER && parseInt(item.selectedValue, 10) > 0) {
        setInitialType(isocenterTypeConstants.whole_body);
        setIsocenterInitialValue(parseInt(item.selectedValue, 10));
      }

      // fetch motion options parameters
      if (MOTION_NAMES.includes(item.name)) {
        updatedMotionParameters.push(item);
      }

      if (CONTRAST_NAMES.includes(item.name)) {
        updatedContrastParameters.push(item);
      }

      if (DEFAULT_CARDIAC_CONFIG_NAMES.includes(item.name)) {
        updatedDefaultCardiacPhaseParameters.push(item);
      }
      return null;
    });

    setDefaultCardiacPhaseParameters(updatedDefaultCardiacPhaseParameters);
    setMotionParameters(updatedMotionParameters);
    setContrastParameters(updatedContrastParameters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionData, modalVisible.type]);

  const handleDropdownItemClick = (action: string, value: string, configurationName: string) => {
    switch (action) {
      case simulatorNavDropdownActions.open_patient_settings:
        // open patient settings modal
        setPatientSettingsModalVisible(true);
        break;
      case simulatorNavDropdownActions.open_scanner_settings:
        // open scanner settings modal
        setScannerSettingsModalVisible(true);
        break;
      case simulatorNavDropdownActions.open_simulation_settings:
        // open simulation settings modal
        setSimulationSettingsModalVisible(true);
        break;
      case simulatorNavDropdownActions.open_image_viewer:
        // history.push(`/session/${sessionId}/image-viewer`);
        window.open(`/session/${sessionId}/image-viewer`, '_blank')?.focus();
        break;
      case simulatorNavDropdownActions.open_tissues_characteristics:
        history.push(`/tissue-characteristics/${sessionId}`);
        break;
      // isotropic options
      case simulatorNavDropdownActions.set_isotropic_0:
        activeSessionStoreActions.dispatch(
          standardSequencesActions.updateConfiguration(sessionData.configuration, configurationName, value)
        );
        activeSessionStoreActions.dispatch(
          standardSequencesActions.updateConfiguration(sessionData.configuration, simulatorConfig.GRID_SIZE_OPTION, '0')
        );
        activeSessionStoreActions.dispatch(
          sessionActions.updateSessionConfiguration(sessionId, sessionData.configuration, configurationName, value)
        );
        activeSessionStoreActions.dispatch(
          sessionActions.updateSessionConfiguration(
            sessionId,
            sessionData.configuration,
            simulatorConfig.GRID_SIZE_OPTION,
            '0'
          )
        );
        break;
      case simulatorNavDropdownActions.set_isotropic_1:
        activeSessionStoreActions.dispatch(
          standardSequencesActions.updateConfiguration(sessionData.configuration, configurationName, value)
        );
        activeSessionStoreActions.dispatch(
          standardSequencesActions.updateConfiguration(
            sessionData.configuration,
            simulatorConfig.GRID_SIZE_OPTION,
            '1mm'
          )
        );
        activeSessionStoreActions.dispatch(
          sessionActions.updateSessionConfiguration(sessionId, sessionData.configuration, configurationName, value)
        );
        activeSessionStoreActions.dispatch(
          sessionActions.updateSessionConfiguration(
            sessionId,
            sessionData.configuration,
            simulatorConfig.GRID_SIZE_OPTION,
            '1mm'
          )
        );
        break;
      case simulatorNavDropdownActions.set_isotropic_05:
        activeSessionStoreActions.dispatch(
          standardSequencesActions.updateConfiguration(sessionData.configuration, configurationName, value)
        );
        activeSessionStoreActions.dispatch(
          standardSequencesActions.updateConfiguration(
            sessionData.configuration,
            simulatorConfig.GRID_SIZE_OPTION,
            '0p5mm'
          )
        );
        activeSessionStoreActions.dispatch(
          sessionActions.updateSessionConfiguration(sessionId, sessionData.configuration, configurationName, value)
        );
        activeSessionStoreActions.dispatch(
          sessionActions.updateSessionConfiguration(
            sessionId,
            sessionData.configuration,
            simulatorConfig.GRID_SIZE_OPTION,
            '0p5mm'
          )
        );
        break;
      case simulatorNavDropdownActions.set_isotropic_optimal:
        activeSessionStoreActions.dispatch(
          standardSequencesActions.updateConfiguration(sessionData.configuration, configurationName, value)
        );
        activeSessionStoreActions.dispatch(
          standardSequencesActions.updateConfiguration(
            sessionData.configuration,
            simulatorConfig.GRID_SIZE_OPTION,
            'optional'
          )
        );
        activeSessionStoreActions.dispatch(
          sessionActions.updateSessionConfiguration(sessionId, sessionData.configuration, configurationName, value)
        );
        activeSessionStoreActions.dispatch(
          sessionActions.updateSessionConfiguration(
            sessionId,
            sessionData.configuration,
            simulatorConfig.GRID_SIZE_OPTION,
            'optional'
          )
        );
        break;
      // motion options
      case simulatorNavDropdownActions.set_motion_options:
        // open motion options modal
        setMotionModalVisible(true);
        break;
      // default cardiac phase
      case simulatorNavDropdownActions.set_default_cardiac_phase:
        // open default cardiac phase modal
        setDefaultCardiacPhaseModalVisible(true);
        break;
      // isocenter
      case simulatorNavDropdownActions.open_iso_center_modal_whole_body:
        if (!anatomicalModel?.features?.some((f) => f.name === anatomicalModelFeaturesConstants.Isocenter)) return;
        // open isocenter modal
        setModalVisible({
          isVisible: true,
          type: isocenterTypeConstants.whole_body,
        });
        break;
      case simulatorNavDropdownActions.open_iso_center_modal_regions:
        // if anatomicalModelId === 6 (brain) disable action for isocenter
        if (!anatomicalModel?.features?.some((f) => f.name === anatomicalModelFeaturesConstants.Isocenter)) return;
        // open isocenter modal
        setModalVisible({
          isVisible: true,
          type: isocenterTypeConstants.regional,
        });
        break;
      // help view
      case simulatorNavDropdownActions.open_help_view:
        window.open('https://help.corsmed.com/', '_blank', 'noopener,noreferrer')?.focus();
        break;
      case simulatorNavDropdownActions.open_3D_viewer:
        modalPWStoreActions.setValue(true);
        break;
      default:
        activeSessionStoreActions.dispatch(
          standardSequencesActions.updateConfiguration(sessionData.configuration, configurationName, value)
        );
        activeSessionStoreActions.dispatch(
          sessionActions.updateSessionConfiguration(sessionId, sessionData.configuration, configurationName, value)
        );
        break;
    }
  };

  const onSaveClickHandler = (checked: string, isocenterType: string) => {
    if (isocenterType === isocenterTypeConstants.whole_body) {
      activeSessionStoreActions.dispatch(
        sessionActions.updateSessionConfiguration(
          sessionId,
          sessionData.configuration,
          simulatorConfig.ISOCENTER,
          checked
        )
      );
      activeSessionStoreActions.dispatch(
        sessionActions.updateSessionConfiguration(
          sessionId,
          sessionData.configuration,
          simulatorConfig.SELECTED_REGION,
          '0'
        )
      );
    } else {
      activeSessionStoreActions.dispatch(
        sessionActions.updateSessionConfiguration(
          sessionId,
          sessionData.configuration,
          simulatorConfig.SELECTED_REGION,
          checked
        )
      );
    }
    setModalVisible({ isVisible: false, type: '' });
  };

  const onSubmit = (values: UniversalParameterProps) => {
    if (!isEmpty(values) && !isEmpty(motionParameters)) {
      // update motion values
      motionParameters.forEach((motionParam: ExperimentConfiguration) => {
        if (values[motionParam.name] === undefined) return;
        activeSessionStoreActions.dispatch(
          sessionActions.updateSessionConfiguration(
            sessionId,
            sessionData.configuration,
            motionParam.name,
            values[motionParam.name].toString()
          )
        );
      });
      const parameters: ExperimentConfiguration[] = [];
      sessionData?.configuration?.forEach((item: ExperimentConfiguration) => {
        // fetch motion options parameters
        const motionNames: string[] = [
          simulatorConfig.MOTION_ROT_ANGLE,
          simulatorConfig.MOTION_ROT_FREQ,
          simulatorConfig.MOTION_TRANS_MAGN,
          simulatorConfig.MOTION_TRANS_FREQ,
          simulatorConfig.MOTION_TRANS_AXIS,
        ];
        if (motionNames.indexOf(item.name) > -1) {
          parameters.push(item);
        }
      });
      setMotionParameters(parameters);
    }

    if (!isEmpty(values) && !isEmpty(contrastParameters)) {
      // update motion values
      contrastParameters.forEach((contrastParam: ExperimentConfiguration) => {
        if (values[contrastParam.name] === undefined) return;
        activeSessionStoreActions.dispatch(
          sessionActions.updateSessionConfiguration(
            sessionId,
            sessionData.configuration,
            contrastParam.name,
            values[contrastParam.name].toString()
          )
        );
      });
      const parameters: ExperimentConfiguration[] = [];
      sessionData?.configuration?.forEach((item: ExperimentConfiguration) => {
        // fetch motion options parameters
        const contrastNames: string[] = [
          simulatorConfig.ACTIVATE_CONTRAST,
          simulatorConfig.CONTRAST_CONCENTRATION,
          simulatorConfig.CONTRAST_VOLUME,
          simulatorConfig.TIME_AFTER_CONTRAST,
        ];
        if (contrastNames.indexOf(item.name) > -1) {
          parameters.push(item);
        }
      });
      setContrastParameters(parameters);
    }
  };
  const debouncedPatientSettingsSubmit = debounce(onSubmit, 350, { leading: false, trailing: true });

  const onSubmitDefaultCardiacPhase = (values: { default_cardiac_phase_value: string }) => {
    const val = values.default_cardiac_phase_value;
    activeSessionStoreActions.dispatch(
      sessionActions.updateSessionConfiguration(
        sessionId,
        sessionData.configuration,
        simulatorConfig.DEFAULT_CARDIAC_PHASE,
        val.toString()
      )
    );
  };
  const debouncedCardiacPhase = debounce(onSubmitDefaultCardiacPhase, 350, { leading: false, trailing: true });

  const handleClickOutside = (event: MouseEvent) => {
    if (navRef.current && !navRef?.current.contains(event.target as Node)) {
      setVisibleMenu('');
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <nav className="navbar navbar-expand-lg p-0" ref={navRef}>
      <div className="navbar-collapse bg-white py-3 px-4" id="navbarContent">
        {!sessionData?.lab?.anatomicalModelId ? (
          <div className="w-100 text-center">
            <Spinner variant="primary" size="sm" />
          </div>
        ) : (
          <ul className="navbar-nav simulator-left-nav">
            <div className="logo">
              <img src={Logo} alt="Corsmed" className="img-thumbnail border-0 shadow-none" />
            </div>
            {menuItems.map((menuItem) =>
              menuItem.subMenu ? (
                <DropdownNavItem
                  key={menuItem.title}
                  item={menuItem}
                  visibleMenu={visibleMenu}
                  onToggle={(name: string) => {
                    if (name !== visibleMenu) {
                      setVisibleMenu(name);
                    }
                  }}
                  handleItemClick={handleDropdownItemClick}
                  config={sessionData?.configuration}
                  model={anatomicalModel}
                />
              ) : (
                <NavItem key={menuItem.title} item={menuItem} handleItemClick={handleDropdownItemClick} />
              )
            )}
          </ul>
        )}
        <ul className="ms-auto d-flex align-items-center navbar-nav simulator-right-nav">
          {/* {sessionData?.assignments && (
            <li className="nav-item">
              <button className="nav-link simulator-link-tertiary" type="button" onClick={onViewAssignmentsHandler}>
                <span className="lab-first-char me-3">{sessionData?.lab?.name.charAt(0)}.</span>
                View assignments
                <i className="fa fa-caret-down ms-3" />
              </button>
            </li>
          )} */}

          {sessionData?.lab?.anatomicalModelId && (
            <li className="nav-item" style={{ marginRight: 30 }}>
              <ChatButton />
            </li>
          )}
          {!!sessionData?.pulseSequences?.length && !isLiteSim && (
            <li className={`nav-item ${newScanDisabled ? 'disabled' : ''}`}>
              <button
                className={`nav-link simulator-link-secondary ${newScanDisabled ? 'not-allowed-cursor disabled' : ''}`}
                disabled={newScanDisabled}
                type="button"
                onClick={onNewScanHandler}
              >
                New Scan
              </button>
            </li>
          )}
          {/* We are removing Reset button for now
          <li className="nav-item">
            <button
              className={`nav-link simulator-link-secondary ${resetDisabled ? 'not-allowed-cursor' : ''}`}
              disabled={resetDisabled}
              type="button"
              onClick={onResetHandler}
            >
              Reset
            </button>
          </li> */}
          {sessionData?.lab?.anatomicalModelId && (
            <li className="nav-item">
              <button
                disabled={closing}
                className="nav-link simulator-link-primary"
                onClick={onCloseSessionHandler}
                onKeyDown={onCloseSessionHandler}
                type="button"
              >
                <span className="me-1">{closing ? 'Closing session..' : 'Save and close'}</span>
                {closing && <Spinner className="closing-spinner" size="sm" animation="border" />}
              </button>
            </li>
          )}
        </ul>
      </div>
      <IsocenterModal
        size="xl"
        centered
        vertical={false}
        fixed="right"
        isOpen={modalVisible.isVisible}
        anatomicalModel={anatomicalModel}
        initialType={initialType}
        type={modalVisible.type}
        toggle={() => {
          setModalVisible({
            isVisible: !modalVisible.isVisible,
            type: '',
          });
        }}
        onSaveClickHandler={onSaveClickHandler}
        isocenterInitialValue={isocenterInitialValue}
        anatomicalModelId={sessionData?.lab?.anatomicalModelId as number}
      />
      {motionParameters && motionParameters.length > 4 && (
        <MotionModal
          size="lg"
          centered
          vertical={false}
          fixed="right"
          isOpen={motionModalVisible}
          toggle={() => {
            setMotionModalVisible(!motionModalVisible);
          }}
          onSubmit={onSubmit}
          // onSubmit={() => onSubmit(motionParameters)}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          motionParameters={motionParameters.filter((v: any, i: any, a: any) => a.indexOf(v) === i)}
        />
      )}
      {defaultCardiacPhaseParameters && defaultCardiacPhaseParameters.length > 0 && (
        <DefaultCardiacPhaseModal
          size="lg"
          centered
          vertical={false}
          fixed="right"
          isOpen={defaultCardiacPhaseModalVisible}
          toggle={() => {
            setDefaultCardiacPhaseModalVisible(!defaultCardiacPhaseModalVisible);
          }}
          onSubmit={onSubmitDefaultCardiacPhase}
          defaultCardiacPhaseParameters={defaultCardiacPhaseParameters.filter(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (v: any, i: any, a: any) => a.indexOf(v) === i
          )}
        />
      )}
      {scannerSettingsModalVisible && (
        <ScannerSettingsModal
          updateScannerSettings={handleDropdownItemClick}
          config={sessionData?.configuration}
          additionalCoils={additionalCoils}
          onClose={() => setScannerSettingsModalVisible(false)}
          anatomicalModel={anatomicalModel}
        />
      )}
      {motionParameters && motionParameters.length > 4 && patientSettingsModalVisible && (
        <PatientSettingsModal
          updatePatientSettings={handleDropdownItemClick}
          config={sessionData?.configuration}
          motionParameters={motionParameters}
          contrastParameters={contrastParameters}
          tissueCharacteristics={tissueCharacteristics.chars}
          onClose={() => setPatientSettingsModalVisible(false)}
          onSubmit={debouncedPatientSettingsSubmit}
          onSubmitCell={submitCellHandler}
          resetTissueCharacteristics={resetTissueCharacteristicsHandler}
          anatomicalModel={anatomicalModel}
          onDefaultCardiacPhaseChange={debouncedCardiacPhase}
        />
      )}
      {simulationSettingsModalVisible && (
        <SimulationSettingsModal
          config={sessionData?.configuration}
          setConfigParam={handleDropdownItemClick}
          onClose={() => setSimulationSettingsModalVisible(false)}
          anatomicalModel={anatomicalModel}
        />
      )}
    </nav>
  );
};

export default Menu;
